<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap">
      <h1><v-icon color="primary" large>mdi-chart-bar</v-icon> Reportes y Exportación</h1>
    </div>

    <!-- ══ Tabs de reportes ═══════════════════════════════════════════════════ -->
    <v-tabs v-model="tab" color="primary" grow class="mb-4" rounded>
      <v-tab><v-icon left>mdi-account-group</v-icon> Miembros</v-tab>
      <v-tab><v-icon left>mdi-currency-usd</v-icon> Pagos</v-tab>
      <v-tab><v-icon left>mdi-calendar-check</v-icon> Visitas</v-tab>
    </v-tabs>

    <!-- ══ Filtros comunes ═══════════════════════════════════════════════════ -->
    <v-card rounded="xl" class="elevation-2 pa-4 mb-4">
      <v-row align="center" dense>

        <!-- Períodos predefinidos -->
        <v-col cols="12" md="6">
          <div class="text-caption text--secondary font-weight-bold mb-1 text-uppercase">Período</div>
          <v-btn-toggle v-model="periodo" mandatory color="primary" rounded dense class="flex-wrap">
            <v-btn value="day"     x-small class="ma-1">Hoy</v-btn>
            <v-btn value="week"    x-small class="ma-1">7 días</v-btn>
            <v-btn value="2weeks"  x-small class="ma-1">15 días</v-btn>
            <v-btn value="month"   x-small class="ma-1">Mes</v-btn>
            <v-btn value="6months" x-small class="ma-1">6 meses</v-btn>
            <v-btn value="year"    x-small class="ma-1">1 año</v-btn>
            <v-btn value="custom"  x-small class="ma-1">Personalizado</v-btn>
          </v-btn-toggle>
        </v-col>

        <!-- Fechas personalizadas -->
        <v-col cols="6" md="2" v-show="periodo === 'custom'">
          <v-text-field v-model="fechaInicio" label="Desde" type="date"
            outlined dense hide-details @change="cargar" />
        </v-col>
        <v-col cols="6" md="2" v-show="periodo === 'custom'">
          <v-text-field v-model="fechaFin" label="Hasta" type="date"
            outlined dense hide-details @change="cargar" />
        </v-col>

        <!-- Filtro estado miembros -->
        <v-col cols="12" sm="6" md="3" v-show="tab === 0">
          <v-select v-model="filtroEstado" :items="['Todos','ACTIVO','VENCIDO']"
            label="Estado" outlined dense hide-details />
        </v-col>

        <!-- Botón cargar -->
        <v-col cols="12" md="1" class="d-flex">
          <v-btn color="primary" rounded :loading="cargando" @click="cargar" class="mt-1">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Rango activo -->
      <div class="text-caption text--secondary mt-2">
        <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>
        {{ fechaInicio }} → {{ fechaFin }}
        <v-chip x-small color="primary" dark class="ml-2">{{ totalRegistros }} registros</v-chip>
      </div>
    </v-card>

    <!-- ══ Botones de exportación ═══════════════════════════════════════════ -->
    <div class="d-flex mb-4 flex-wrap" style="gap:8px">
      <v-btn color="success" rounded :loading="exportando === 'xl'" @click="exportar('excel')">
        <v-icon left>mdi-microsoft-excel</v-icon> Exportar Excel
      </v-btn>
      <v-btn color="error" rounded :loading="exportando === 'pdf'" @click="exportar('pdf')">
        <v-icon left>mdi-file-pdf-box</v-icon> Exportar PDF
      </v-btn>
    </div>

    <!-- ══ TABLA: MIEMBROS ════════════════════════════════════════════════════ -->
    <v-tab-item>
      <v-card rounded="xl" class="elevation-2">
        <v-data-table
          :headers="headersMiembros"
          :items="datos"
          :loading="cargando"
          dense
          class="rounded-xl"
          :footer-props="{ itemsPerPageText:'Por página', itemsPerPageOptions:[10,25,50,-1] }"
        >
          <template #item.estado="{ item }">
            <v-chip x-small :color="item.estado==='ACTIVO'?'success':'error'" dark>
              {{ item.estado }}
            </v-chip>
          </template>
          <template #item.precio="{ item }">
            {{ moneda }}{{ parseFloat(item.precio||0).toLocaleString('es-CR') }}
          </template>
          <template #item.diasRestantes="{ item }">
            <v-chip x-small :color="parseInt(item.diasRestantes||0)<=5?'error':parseInt(item.diasRestantes||0)<=15?'orange':'success'" dark>
              {{ item.diasRestantes }}d
            </v-chip>
          </template>

          <!-- KPI summary row -->
          <template #body.append v-if="!cargando && datos.length">
            <tr class="blue-grey lighten-5">
              <td :colspan="headersMiembros.length - 2" class="text-caption font-weight-bold">TOTALES</td>
              <td class="text-caption font-weight-bold">{{ datos.filter(m=>m.estado==='ACTIVO').length }} activos</td>
              <td></td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </v-tab-item>

    <!-- ══ TABLA: PAGOS ══════════════════════════════════════════════════════ -->
    <v-tab-item>
      <v-card rounded="xl" class="elevation-2">
        <!-- KPI pagos -->
        <v-row dense class="pa-4 pb-0">
          <v-col cols="6" sm="3">
            <div class="text-caption text--secondary">Total</div>
            <div class="text-h6 font-weight-bold success--text">{{ moneda }}{{ totalPagos }}</div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="text-caption text--secondary">Registros</div>
            <div class="text-h6 font-weight-bold">{{ datos.length }}</div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="text-caption text--secondary">Promedio</div>
            <div class="text-h6 font-weight-bold">{{ moneda }}{{ promedioPagos }}</div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="text-caption text--secondary">Mayor pago</div>
            <div class="text-h6 font-weight-bold">{{ moneda }}{{ mayorPago }}</div>
          </v-col>
        </v-row>
        <v-divider class="mt-3" />
        <v-data-table
          :headers="headersPagos"
          :items="datos"
          :loading="cargando"
          dense
          class="rounded-xl"
          :footer-props="{ itemsPerPageText:'Por página', itemsPerPageOptions:[10,25,50,-1] }"
        >
          <template #item.monto="{ item }">
            <span class="success--text font-weight-bold">
              {{ moneda }}{{ parseFloat(item.monto||0).toLocaleString('es-CR',{minimumFractionDigits:2}) }}
            </span>
          </template>
        </v-data-table>
      </v-card>
    </v-tab-item>

    <!-- ══ TABLA: VISITAS ════════════════════════════════════════════════════ -->
    <v-tab-item>
      <v-card rounded="xl" class="elevation-2">
        <v-data-table
          :headers="headersVisitas"
          :items="datos"
          :loading="cargando"
          dense
          class="rounded-xl"
          :footer-props="{ itemsPerPageText:'Por página', itemsPerPageOptions:[10,25,50,-1] }"
        />
      </v-card>
    </v-tab-item>

    <v-snackbar v-model="snack.show" :color="snack.color" top :timeout="3500">
      {{ snack.msg }}
    </v-snackbar>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService'

export default {
  name: 'ReportesComponent',
  data() {
    var hoy = new Date()
    var ini = new Date(hoy); ini.setDate(ini.getDate()-30)
    var fmt = function(d) {
      return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0')
    }
    return {
      tab: 0,
      cargando: false,
      exportando: '',
      periodo: 'month',
      fechaInicio: fmt(ini),
      fechaFin:    fmt(hoy),
      filtroEstado: 'Todos',
      datos: [],
      moneda: localStorage.getItem('monedaGimnasio') || '₡',
      ajustes: {},
      snack: { show: false, msg: '', color: 'success' },
    }
  },

  computed: {
    totalRegistros() { return this.datos.length },

    totalPagos() {
      var t = this.datos.reduce(function(s,p){ return s + parseFloat(p.monto||0) }, 0)
      return t.toLocaleString('es-CR', { minimumFractionDigits: 2 })
    },
    promedioPagos() {
      if (!this.datos.length) return '0.00'
      var t = this.datos.reduce(function(s,p){ return s + parseFloat(p.monto||0) }, 0)
      return (t / this.datos.length).toLocaleString('es-CR', { minimumFractionDigits: 2 })
    },
    mayorPago() {
      if (!this.datos.length) return '0.00'
      return Math.max.apply(null, this.datos.map(function(p){ return parseFloat(p.monto||0) }))
           .toLocaleString('es-CR', { minimumFractionDigits: 2 })
    },

    headersMiembros() {
      return [
        { text:'Matrícula',    value:'matricula' },
        { text:'Nombre',       value:'nombre' },
        { text:'Teléfono',     value:'telefono' },
        { text:'Correo',       value:'correo' },
        { text:'Estado',       value:'estado' },
        { text:'Plan',         value:'membresia' },
        { text:'Precio plan',  value:'precio' },
        { text:'Vence',        value:'fechaFin' },
        { text:'Días rest.',   value:'diasRestantes' },
        { text:'Reg. desde',   value:'fechaRegistro' },
      ]
    },
    headersPagos() {
      return [
        { text:'#',          value:'id' },
        { text:'Miembro',    value:'miembro' },
        { text:'Matrícula',  value:'matricula' },
        { text:'Fecha',      value:'fecha' },
        { text:'Monto',      value:'monto' },
        { text:'Plan',       value:'membresia' },
        { text:'Cobrador',   value:'cobrador' },
      ]
    },
    headersVisitas() {
      return [
        { text:'Fecha',           value:'fecha' },
        { text:'Miembro',         value:'miembro' },
        { text:'Matrícula',       value:'matricula' },
        { text:'Plan',            value:'membresia' },
        { text:'Registrado por',  value:'registradoPor' },
      ]
    },
  },

  watch: {
    tab()    { this.cargar() },
    periodo(val) {
      if (val !== 'custom') {
        this.fechaInicio = this.calcularFechaInicio(val, new Date())
        this.fechaFin    = this.fmt(new Date())
        this.cargar()
      }
    },
  },

  mounted() { this.cargar() },

  methods: {
    fmt(d) {
      var mm = String(d.getMonth()+1).padStart(2,'0')
      var dd = String(d.getDate()).padStart(2,'0')
      return d.getFullYear() + '-' + mm + '-' + dd
    },

    calcularFechaInicio(periodo, hoy) {
      var d = new Date(hoy)
      switch (periodo) {
        case 'day':     d.setDate(d.getDate()); break
        case 'week':    d.setDate(d.getDate()-7); break
        case '2weeks':  d.setDate(d.getDate()-14); break
        case 'month':   d.setDate(d.getDate()-30); break
        case '6months': d.setMonth(d.getMonth()-6); break
        case 'year':    d.setFullYear(d.getFullYear()-1); break
        default: break
      }
      return this.fmt(d)
    },

    async cargar() {
      this.cargando = true
      this.datos = []
      try {
        var r = null
        var filtros = { fechaInicio: this.fechaInicio, fechaFin: this.fechaFin }

        if (this.tab === 0) {
          // Miembros
          var f = {}
          if (this.filtroEstado !== 'Todos') f.estado = this.filtroEstado
          r = await HttpService.registrar({ metodo: 'exportar_miembros', filtros: f }, 'reportes.php')
          this.datos = r ? (r.miembros || []) : []
          this.ajustes = r ? (r.ajustes || {}) : {}

        } else if (this.tab === 1) {
          // Pagos
          r = await HttpService.exportarPagos(filtros)
          this.datos = r ? (r.pagos || []) : []
          this.ajustes = r ? (r.ajustes || {}) : {}

        } else if (this.tab === 2) {
          // Visitas
          r = await HttpService.registrar({ metodo: 'exportar_visitas', filtros: filtros }, 'reportes.php')
          this.datos = r ? (r.visitas || []) : []
          this.ajustes = r ? (r.ajustes || {}) : {}
        }
      } catch (e) {
        this.snack = { show: true, msg: 'Error al cargar datos', color: 'error' }
      }
      this.cargando = false
    },

    async exportar(formato) {
      var clave = formato === 'excel' ? 'xl' : 'pdf'
      this.exportando = clave
      try {
        var tipo    = ['miembros','pagos','visitas'][this.tab]
        var aj      = this.ajustes || {}
        var gym     = aj.nombre || 'GymPro'
        var periodo = this.fechaInicio + ' al ' + this.fechaFin

        if (formato === 'excel') {
          await this.excel(tipo, gym, periodo)
        } else {
          await this.pdf(tipo, gym, periodo, aj)
        }
        this.snack = { show: true, msg: 'Archivo generado ✓', color: 'success' }
      } catch(e) {
        this.snack = { show: true, msg: 'Error al exportar: ' + (e.message||''), color: 'error' }
      }
      this.exportando = ''
    },

    async excel(tipo, gym, periodo) {
      var XLSX = await import('xlsx')
      var wb   = XLSX.utils.book_new()
      var rows = []

      if (tipo === 'miembros') {
        rows = this.datos.map(function(m) {
          return { 'Matrícula':m.matricula,'Nombre':m.nombre,'Teléfono':m.telefono,'Correo':m.correo,
            'Estado':m.estado,'Plan':m.membresia,'Precio':m.precio,
            'Vence':m.fechaFin,'Días Rest.':m.diasRestantes,'Desde':m.fechaRegistro }
        })
      } else if (tipo === 'pagos') {
        var mon = this.moneda
        rows = this.datos.map(function(p) {
          return { '#':p.id,'Miembro':p.miembro,'Matrícula':p.matricula,'Fecha':p.fecha,
            'Monto':p.monto,'Plan':p.membresia,'Cobrador':p.cobrador }
        })
      } else {
        rows = this.datos.map(function(v) {
          return { 'Fecha':v.fecha,'Miembro':v.miembro,'Matrícula':v.matricula,
            'Plan':v.membresia,'Registrado por':v.registradoPor }
        })
      }

      if (!rows.length) throw new Error('No hay datos')
      var ws = XLSX.utils.json_to_sheet(rows)
      var tituloHoja = tipo.charAt(0).toUpperCase() + tipo.slice(1)
      XLSX.utils.book_append_sheet(wb, ws, tituloHoja)
      XLSX.writeFile(wb, gym + '_' + tituloHoja + '_' + this.fechaInicio + '.xlsx')
    },

    async pdf(tipo, gym, periodo, aj) {
      var mod  = await import('jspdf')
      var jsPDF= mod.default || mod
      await import('jspdf-autotable')
      var isLandscape = (tipo === 'miembros')
      var doc  = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait' })
      var W    = doc.internal.pageSize.width

      // Header con color
      doc.setFillColor(23, 6, 255)
      doc.rect(0, 0, W, 28, 'F')
      doc.setTextColor(255,255,255)
      doc.setFontSize(16)
      doc.text(gym, 14, 12)
      doc.setFontSize(8)
      doc.text((aj.direccion||'') + '  ·  Tel: ' + (aj.telefono||'') + '  ·  ' + (aj.correo||''), 14, 19)
      doc.setFontSize(10)
      doc.text('Período: ' + periodo, 14, 26)
      doc.setTextColor(0,0,0)

      var titulos = { miembros:'Listado de Miembros', pagos:'Reporte de Pagos', visitas:'Registro de Visitas' }
      doc.setFontSize(14)
      doc.text(titulos[tipo], 14, 38)
      doc.setFontSize(8)
      doc.setTextColor(120,120,120)
      doc.text('Total de registros: ' + this.datos.length + '  ·  Generado: ' + new Date().toLocaleString('es-CR'), 14, 44)

      var head = [], body = []

      if (tipo === 'miembros') {
        head = [['Matrícula','Nombre','Teléfono','Estado','Plan','Vence','Días rest.']]
        body = this.datos.map(function(m){
          return [m.matricula,m.nombre,m.telefono||'—',m.estado,m.membresia||'—',m.fechaFin||'—',m.diasRestantes]
        })
      } else if (tipo === 'pagos') {
        head = [['#','Miembro','Matrícula','Fecha','Monto','Plan','Cobrador']]
        var mon = this.moneda
        body = this.datos.map(function(p){
          return [p.id,p.miembro||'—',p.matricula||'—',p.fecha,mon+(parseFloat(p.monto||0).toLocaleString('es-CR',{minimumFractionDigits:2})),p.membresia||'—',p.cobrador||'—']
        })
        // Fila de totales
        body.push(['','','','TOTAL', mon+this.totalPagos,'',''])
      } else {
        head = [['Fecha','Miembro','Matrícula','Plan','Registrado por']]
        body = this.datos.map(function(v){
          return [v.fecha,v.miembro||'—',v.matricula||'—',v.membresia||'—',v.registradoPor||'—']
        })
      }

      if (!body.length) throw new Error('No hay datos')

      doc.setTextColor(0,0,0)
      doc.autoTable({
        startY: 48,
        head: head,
        body: body,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [23, 6, 255], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 255] },
        foot: tipo === 'pagos' ? [[]] : undefined,
      })

      var fn = gym.replace(/ /g,'_') + '_' + tipo + '_' + this.fechaInicio + '.pdf'
      doc.save(fn)
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/Reportes/ReportesComponent.vue